import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface Promotion {
  id: string;
  name: string;
  description: string;
  type: "buy_x_get_y" | "bundle" | "flash_sale" | "loyalty";
  discount: number;
  startDate: string;
  endDate: string;
  status: "active" | "scheduled" | "expired" | "draft";
  products: number;
  redemptions: number;
}

const MOCK_PROMOTIONS: Promotion[] = [
  {
    id: "1",
    name: "Compra 2 Lleva 3",
    description: "En productos seleccionados de temporada",
    type: "buy_x_get_y",
    discount: 33,
    startDate: "2024-01-01",
    endDate: "2024-03-31",
    status: "active",
    products: 45,
    redemptions: 234,
  },
  {
    id: "2",
    name: "Pack Familiar",
    description: "Combo de productos con descuento especial",
    type: "bundle",
    discount: 25,
    startDate: "2024-02-01",
    endDate: "2024-06-30",
    status: "active",
    products: 12,
    redemptions: 89,
  },
  {
    id: "3",
    name: "Venta Flash Fin de Semana",
    description: "Descuentos exclusivos por tiempo limitado",
    type: "flash_sale",
    discount: 50,
    startDate: "2024-03-15",
    endDate: "2024-03-17",
    status: "scheduled",
    products: 100,
    redemptions: 0,
  },
  {
    id: "4",
    name: "Programa VIP",
    description: "Beneficios exclusivos para clientes frecuentes",
    type: "loyalty",
    discount: 15,
    startDate: "2024-01-01",
    endDate: "2024-12-31",
    status: "active",
    products: 0,
    redemptions: 567,
  },
];

export default function PromotionsSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    cardHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const getTypeIcon = (type: Promotion["type"]) => {
    const icons: Record<Promotion["type"], string> = {
      buy_x_get_y: "ri-shopping-bag-line",
      bundle: "ri-stack-line",
      flash_sale: "ri-flashlight-line",
      loyalty: "ri-vip-crown-line",
    };
    return icons[type];
  };

  const getTypeLabel = (type: Promotion["type"]) => {
    const labels: Record<Promotion["type"], string> = {
      buy_x_get_y: t("posPromotions.typeBuyXGetY"),
      bundle: t("posPromotions.typeBundle"),
      flash_sale: t("posPromotions.typeFlashSale"),
      loyalty: t("posPromotions.typeLoyalty"),
    };
    return labels[type];
  };

  const getTypeColor = (type: Promotion["type"]) => {
    const colors: Record<Promotion["type"], string> = {
      buy_x_get_y: "bg-blue-500/20 text-blue-500",
      bundle: "bg-purple-500/20 text-purple-500",
      flash_sale: "bg-orange-500/20 text-orange-500",
      loyalty: "bg-amber-500/20 text-amber-500",
    };
    return colors[type];
  };

  const getStatusBadge = (status: Promotion["status"]) => {
    const styles: Record<Promotion["status"], string> = {
      active: "bg-emerald-500/20 text-emerald-500",
      scheduled: "bg-blue-500/20 text-blue-500",
      expired: "bg-red-500/20 text-red-500",
      draft: "bg-gray-500/20 text-gray-500",
    };
    const labels: Record<Promotion["status"], string> = {
      active: t("posPromotions.statusActive"),
      scheduled: t("posPromotions.statusScheduled"),
      expired: t("posPromotions.statusExpired"),
      draft: t("posPromotions.statusDraft"),
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const filteredPromotions = MOCK_PROMOTIONS.filter((promo) => {
    const matchesSearch = promo.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "all" || promo.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const activeCount = MOCK_PROMOTIONS.filter((p) => p.status === "active").length;
  const totalRedemptions = MOCK_PROMOTIONS.reduce((acc, p) => acc + p.redemptions, 0);
  const scheduledCount = MOCK_PROMOTIONS.filter((p) => p.status === "scheduled").length;

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>
            {t("posPromotions.title")}
          </h1>
          <p className={themeClasses.textSecondary}>
            {t("posPromotions.subtitle")}
          </p>
        </div>
        <button className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2">
          <i className="ri-add-line"></i>
          {t("posPromotions.createPromotion")}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posPromotions.activePromotions")}</span>
            <i className="ri-gift-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{activeCount}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posPromotions.totalRedemptions")}</span>
            <i className="ri-ticket-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{totalRedemptions}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posPromotions.scheduled")}</span>
            <i className="ri-calendar-check-line text-purple-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{scheduledCount}</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("posPromotions.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
          >
            <option value="all">{t("posPromotions.allTypes")}</option>
            <option value="buy_x_get_y">{t("posPromotions.typeBuyXGetY")}</option>
            <option value="bundle">{t("posPromotions.typeBundle")}</option>
            <option value="flash_sale">{t("posPromotions.typeFlashSale")}</option>
            <option value="loyalty">{t("posPromotions.typeLoyalty")}</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
          {filteredPromotions.map((promo) => (
            <div
              key={promo.id}
              className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-4 ${themeClasses.cardHover} transition-colors cursor-pointer`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getTypeColor(promo.type)}`}>
                    <i className={`${getTypeIcon(promo.type)} text-xl`}></i>
                  </div>
                  <div>
                    <h3 className={`font-semibold ${themeClasses.text}`}>{promo.name}</h3>
                    <p className={`text-sm ${themeClasses.textSecondary}`}>{promo.description}</p>
                  </div>
                </div>
                {getStatusBadge(promo.status)}
              </div>

              <div className="flex items-center gap-4 mb-3">
                <span className={`px-2 py-1 rounded text-xs ${getTypeColor(promo.type)}`}>
                  {getTypeLabel(promo.type)}
                </span>
                <span className="text-primary-500 font-bold text-lg">{promo.discount}% OFF</span>
              </div>

              <div className={`flex items-center justify-between pt-3 border-t ${themeClasses.border}`}>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <i className={`ri-calendar-line ${themeClasses.textSecondary}`}></i>
                    <span className={`text-sm ${themeClasses.textSecondary}`}>
                      {promo.startDate} - {promo.endDate}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {promo.products > 0 && (
                    <span className={`text-sm ${themeClasses.textSecondary}`}>
                      <i className="ri-shopping-bag-line mr-1"></i>
                      {promo.products}
                    </span>
                  )}
                  <span className={`text-sm ${themeClasses.textSecondary}`}>
                    <i className="ri-ticket-line mr-1"></i>
                    {promo.redemptions}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
